/**
 * TIẾN TRÌNH MÀN CHƠI — LOGIC THUẦN, 0 dòng Phaser (đúng luật kiến trúc README).
 *
 * File này KHÔNG định nghĩa lại luật mở khoá và KHÔNG chép bảng thưởng: nó gọi
 * `isUnlocked()` của `data/maps.ts`. Nhờ vậy UI, save và sim dùng CHUNG một luật — bẫy
 * "luật chép 3 bản rồi lệch nhau" đã gặp và ghi lại ở `data/maps.ts`.
 *
 * `ProgressState` là CẤU TRÚC TỐI THIỂU (structural typing): `MetaState` thật trong
 * `sim/meta.ts` chỉ cần có `clearedMaps` + `diamonds` + `shards` là thoả, không cần kế thừa.
 * Nhờ vậy module này test được bằng object 3 dòng, và save cũ thiếu field vẫn chạy.
 */
import {
  DIFFICULTIES,
  MAPS,
  isUnlocked,
  mapById,
  mapKey,
  parseKey,
  pickShardTargets,
  type Difficulty,
  type MapDef,
  type RewardRoll,
} from '../data/maps';

export interface ProgressState {
  /** khoá đã qua, dạng `'1.1:easy'` */
  clearedMaps: string[];
  diamonds: number;
  shards: { hero: Record<string, number>; skill: Record<string, number> };
  /**
   * TUỲ CHỌN — nơi ghi ĐỒ HOÀN CHỈNH (id skill/ class vừa trúng).
   * Vì sao để optional: đặc tả chỉ yêu cầu tối thiểu clearedMaps/ diamonds/ shards, nên
   * `MetaState` không có field này vẫn truyền được vào mọi hàm; ai muốn lưu đồ hoàn chỉnh
   * thì thêm field là tự động được ghi. Không có field ⇒ hàm bỏ qua, KHÔNG ném lỗi.
   */
  fullItems?: string[];
}

/** Khoá gom mảnh khi không xác định được class/ món cụ thể — mảnh vẫn phải được đếm. */
const ANY = '*';

// ------------------------------------------------------------------ đọc dị dạng an toàn

/** Đọc `clearedMaps` chịu được save rác. Không BAO GIỜ ném. */
function clearedList(state: ProgressState | undefined | null): string[] {
  const list = (state as ProgressState | undefined)?.clearedMaps;
  return Array.isArray(list) ? list : [];
}

/**
 * Tự vá state thiếu field (save cũ/ save hỏng) trước khi GHI.
 * Vì sao cần: save từ bản trước chưa có `diamonds`/ `shards`; nếu cộng thẳng vào `undefined`
 * thì `NaN` lan ra toàn bộ kinh tế và người chơi mất sạch tiến trình.
 * Trả `false` nếu state bị đông cứng (không ghi được) ⇒ người gọi DỪNG, thà mất 1 lần thưởng
 * còn hơn làm vỡ vòng lặp game.
 */
function safeEnsure(state: ProgressState): boolean {
  try {
    const s = state as unknown as Record<string, unknown>;
    if (!Array.isArray(s.clearedMaps)) s.clearedMaps = [];
    if (typeof s.diamonds !== 'number' || !Number.isFinite(s.diamonds)) s.diamonds = 0;
    const sh = s.shards as Record<string, unknown> | undefined;
    if (!sh || typeof sh !== 'object') {
      s.shards = { hero: {}, skill: {} };
    } else {
      for (const k of ['hero', 'skill'] as const) {
        if (!sh[k] || typeof sh[k] !== 'object') sh[k] = {};
      }
    }
    return true;
  } catch {
    return false;
  }
}

// ------------------------------------------------------------------ trạng thái 1 ô

/** Ô (màn × độ khó) này đã qua chưa? Khoá rác bị bỏ qua. */
export function isMapCleared(state: ProgressState, mapId: string, diff: Difficulty): boolean {
  const key = mapKey(mapId, diff);
  return clearedList(state).some((k) => k === key && parseKey(k) !== null);
}

/**
 * Đánh dấu đã qua màn — **IDEMPOTENT**: gọi 100 lần vẫn chỉ có 1 khoá.
 * Vì sao cần idempotent: `world.ts` có thể gọi lúc Nhà thủng, UI gọi lại khi vẽ bảng, và
 * người chơi bấm "Chơi lại" rồi thắng lần nữa. Không idempotent ⇒ `clearedMaps` phình ra và
 * `completionPct` vượt 100%.
 */
export function markCleared(state: ProgressState, mapId: string, diff: Difficulty): void {
  // Màn lạ HOẶC độ khó lạ ⇒ bỏ qua, không ghi rác vào save.
  // ⚠️ Bẫy đã sập: chỉ kiểm tra màn, quên kiểm tra độ khó ⇒ save có khoá `'1.1:không-có'`,
  // `parseKey` từ chối nó nên nó nằm lại trong save mãi mà không bao giờ tính là đã qua.
  if (!mapById(mapId)) return;
  if (!DIFFICULTIES.some((d) => d.id === diff)) return;
  if (!safeEnsure(state)) return;
  const list = clearedList(state);
  const key = mapKey(mapId, diff);
  if (!list.includes(key)) list.push(key);
}

/**
 * ÁP 1 LẦN QUAY THƯỞNG vào state: cộng kim cương + cộng dồn mảnh theo class/ món.
 *
 * ⚠️ CỐ Ý **KHÔNG** idempotent — gọi 2 lần là cộng 2 lần (khác `markCleared`).
 * Vì sao: hàm này là "trao thưởng", người gọi chịu trách nhiệm gọi đúng 1 lần mỗi lần qua màn
 * (giống `addGold`). Nếu làm idempotent thì phải nhớ "đã thưởng ô nào" ⇒ thêm state, thêm
 * migrate, mà chẳng giải quyết vấn đề gì (chơi lại màn cũ vẫn phải thưởng).
 *
 * Mảnh không xác định được đích (người gọi đưa danh sách rỗng) vẫn được cộng vào khoá `'*'`
 * để không bốc hơi khỏi save.
 */
export function applyReward(
  state: ProgressState,
  roll: RewardRoll,
  targets: ReturnType<typeof pickShardTargets>,
): void {
  if (!safeEnsure(state)) return;
  const shards = state.shards;

  const add = (bucket: Record<string, number>, key: string | undefined, n: unknown): void => {
    const amount = Math.floor(Number(n));
    if (!Number.isFinite(amount) || amount <= 0) return; // số 0/ rác ⇒ không tạo khoá rác trong save
    const k = key && typeof key === 'string' ? key : ANY;
    bucket[k] = (bucket[k] ?? 0) + amount;
  };

  state.diamonds += Math.max(0, Math.floor(Number(roll?.diamonds) || 0));

  add(shards.hero, targets?.heroClass, roll?.heroShards);
  add(shards.skill, targets?.skillId ?? targets?.skillClass, roll?.skillShards);

  // Đồ HOÀN CHỈNH: không phải mảnh, nên vào `fullItems` (nếu state có chỗ chứa). Khử trùng để
  // mặc cùng 1 món 2 lần không thành 2 dòng rác trong kho.
  const fullItem = roll?.fullItem;
  const fullId = targets?.fullItemId;
  if (fullItem && fullId && Array.isArray(state.fullItems) && !state.fullItems.includes(fullId)) {
    state.fullItems.push(fullId);
  }
}

// ------------------------------------------------------------------ cho UI

export interface MapProgress {
  map: MapDef;
  difficulty: Difficulty;
  unlocked: boolean;
  cleared: boolean;
}

/**
 * LƯỚI TIẾN TRÌNH cho màn hình chọn màn: đủ 15 màn × 3 độ khó = **45 ô**, theo thứ tự
 * màn trước → màn sau, trong mỗi màn là easy → normal → elite.
 *
 * Vì sao trả lưới phẳng thay vì tự vẽ trong UI: UI chỉ việc duyệt mảng, không cần biết luật
 * mở khoá; và test khoá được "45 ô, cờ khớp `isUnlocked`" bằng số đo.
 */
export function progressGrid(state: ProgressState): MapProgress[] {
  const cleared = clearedList(state);
  const out: MapProgress[] = [];
  for (const map of MAPS) {
    for (const d of DIFFICULTIES) {
      out.push({
        map,
        difficulty: d.id,
        unlocked: isUnlocked(cleared, map.id, d.id),
        cleared: isMapCleared(state, map.id, d.id),
      });
    }
  }
  return out;
}

/**
 * GỢI Ý Ô NÊN CHƠI TIẾP: ô ĐẦU TIÊN đang mở mà chưa qua.
 * Vì sao duyệt theo thứ tự lưới: sau khi qua `1.1 easy`, ô đầu tiên mở-mà-chưa-qua là
 * `1.1 normal` ⇒ UI tự nhiên chỉ người chơi leo độ khó trước, rồi mới sang màn sau —
 * đúng luật "qua Dễ mới được chọn Thường". Qua hết ⇒ `null`.
 */
export function nextObjective(state: ProgressState): { mapId: string; diff: Difficulty } | null {
  for (const cell of progressGrid(state)) {
    if (cell.unlocked && !cell.cleared) return { mapId: cell.map.id, diff: cell.difficulty };
  }
  return null;
}

/** Tổng số ô có thể qua = 15 màn × 3 độ khó. */
export const TOTAL_CELLS = MAPS.length * DIFFICULTIES.length;

/**
 * % HOÀN THÀNH (0..100), làm tròn 1 chữ số thập phân.
 * Chỉ đếm khoá HỢP LỆ và nằm trong lưới hiện tại ⇒ save cũ giữ khoá của màn đã xoá không thể
 * đẩy con số vượt 100%.
 */
export function completionPct(state: ProgressState): number {
  const valid = new Set(progressGrid(state).filter((c) => c.cleared).map((c) => mapKey(c.map.id, c.difficulty)));
  const pct = (valid.size / TOTAL_CELLS) * 100;
  return Math.round(pct * 10) / 10;
}
